import { Box, Typography, Button } from "@mui/material";
import { ErrorBoundary } from "@sentry/react";
import React, { useEffect, useMemo } from "react";
import { isDeployBundleMismatchError } from "../../utils/deployErrorDetector";

const AUTO_RELOAD_FLAG_KEY = "propally_agent:auto_reload_on_error:v1";

const safeGetSessionFlag = (key: string) => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSetSessionFlag = (key: string, value: string) => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // ignore
  }
};

const performHardReload = () => {
  const url = new URL(window.location.href);
  url.searchParams.set("__reload", Date.now().toString());
  window.location.replace(url.toString());
};

const errorToNameAndMessage = (errorLike: unknown): { name: string; message: string } => {
  if (errorLike instanceof Error) {
    return { name: errorLike.name ?? "", message: errorLike.message ?? "" };
  }
  if (typeof errorLike === "string") {
    return { name: "", message: errorLike };
  }
  if (errorLike && typeof errorLike === "object") {
    const maybeName = "name" in errorLike ? String((errorLike as { name?: unknown }).name ?? "") : "";
    const maybeMessage = "message" in errorLike ? String((errorLike as { message?: unknown }).message ?? "") : "";
    return { name: maybeName, message: maybeMessage };
  }
  return { name: "", message: "" };
};

function ErrorFallback({ error }: { error: unknown }) {
  const { name, message } = errorToNameAndMessage(error);
  const isMismatchError = useMemo(() => isDeployBundleMismatchError(error), [error]);
  const autoReloadAlreadyAttempted = useMemo(() => safeGetSessionFlag(AUTO_RELOAD_FLAG_KEY) === "1", []);

  const onForceReload = () => {
    safeSetSessionFlag(AUTO_RELOAD_FLAG_KEY, "1");
    performHardReload();
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="100vh" p={4}>
      <Typography variant="h5" color="error" mb={2}>
        エラーが発生しました
      </Typography>

      <Typography variant="body1" color="text.secondary" mb={3} textAlign="center">
        {isMismatchError && (
          <>
            古い画面が残っている可能性があります。 <br />
            下のボタンから画面を更新してください。
            <br />
            <br />
          </>
        )}
        申し訳ありません。予期しないエラーが発生しました。 <br />
        下のボタンから画面を更新してください。
        <br />
        それでも解決しない場合は、お手数おかけしますが, 以下エラー内容を添付の上お問い合わせください。
      </Typography>

      <Box sx={{ display: "flex", gap: 1, mb: 3, flexWrap: "wrap", justifyContent: "center" }}>
        <Button variant="contained" onClick={onForceReload}>
          画面を更新する
        </Button>
      </Box>

      <Box p={2} bgcolor="grey.100" borderRadius={1} width="100%" maxWidth={600} mb={3}>
        <Typography
          variant="body2"
          color="text.secondary"
          component="pre"
          style={{
            wordBreak: "break-word",
            whiteSpace: "pre-wrap",
            fontSize: "12px",
          }}
        >
          エラー名: {name}
          {"\n"}エラー内容: {message}
          {"\n"}発生時刻: {new Date().toLocaleString("ja-JP")}
          {"\n"}自動リロード試行: {autoReloadAlreadyAttempted ? "あり" : "なし"}
          {error instanceof Error && error.stack && `\nスタックトレース:\n${error.stack}`}
        </Typography>
      </Box>
    </Box>
  );
}

interface AppErrorBoundaryProps {
  children: React.ReactNode;
}

export default function AppErrorBoundary({ children }: AppErrorBoundaryProps) {
  useEffect(() => {
    if (process.env.NODE_ENV !== "production") return;

    const tryAutoReload = (errorLike: unknown) => {
      if (!isDeployBundleMismatchError(errorLike)) return;
      if (safeGetSessionFlag(AUTO_RELOAD_FLAG_KEY) === "1") return;

      safeSetSessionFlag(AUTO_RELOAD_FLAG_KEY, "1");
      performHardReload();
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      tryAutoReload(event.reason);
    };

    const onErrorEvent = (event: ErrorEvent) => {
      tryAutoReload(event.error ?? event.message);
    };

    window.addEventListener("unhandledrejection", onUnhandledRejection);
    window.addEventListener("error", onErrorEvent);

    return () => {
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
      window.removeEventListener("error", onErrorEvent);
    };
  }, []);

  return (
    <ErrorBoundary
      fallback={({ error }) => <ErrorFallback error={error} />}
      beforeCapture={(scope, error) => {
        if (isDeployBundleMismatchError(error)) {
          scope.setTag("error_cause", "deploy_bundle_mismatch");
          scope.setTag("auto_reload_attempted", safeGetSessionFlag(AUTO_RELOAD_FLAG_KEY) === "1" ? "true" : "false");
        }
      }}
      onError={(error) => {
        if (process.env.NODE_ENV === "production") {
          if (isDeployBundleMismatchError(error) && safeGetSessionFlag(AUTO_RELOAD_FLAG_KEY) !== "1") {
            safeSetSessionFlag(AUTO_RELOAD_FLAG_KEY, "1");
            performHardReload();
          }
        }
      }}
    >
      {children}
    </ErrorBoundary>
  );
}
